import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@porchpass/ui";
import { createContext, useContext, useState, useEffect } from "react";
import type { ReactNode } from "react";
import type { DomainApplicationLenderConfiguration } from "@porchpass/api-client";
import type {
  ApplicationCondition,
  NewConditionInput,
} from "src/types/conditions";
import { useConditions } from "src/hooks/useConditions";
import { isPorchpassLender } from "src/utils/conditionUtils";

interface ConditionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  borrowerName?: string;
  onConfirm: (
    documents: File[],
    selectedLenders: DomainApplicationLenderConfiguration[],
    conditionFiles?: Map<string, File>,
    conditions?: ApplicationCondition[],
    newConditions?: NewConditionInput[],
  ) => Promise<void>;
  lenders: DomainApplicationLenderConfiguration[];
  applicationId?: string;
  children?: ReactNode;
}

interface DocumentContextType {
  documents: File[];
  addDocument: (file: File) => void;
  removeDocument: (fileName: string) => void;
  clearDocuments: () => void;
}

interface SendToLendersContextType {
  lenders: DomainApplicationLenderConfiguration[];
  selectedLenderIndices: number[];
  toggleLender: (index: number) => void;
  clearSelectedLenders: () => void;
}

interface ConditionsContextType {
  conditions: ApplicationCondition[];
  setConditions: (conditions: ApplicationCondition[]) => void;
  conditionFiles: Map<string, File>;
  setConditionFile: (conditionId: string, file: File | null) => void;
  loadingConditions: boolean;
  newConditions: NewConditionInput[];
  addNewCondition: (condition: NewConditionInput) => void;
  removeNewCondition: (title: string) => void;
}

export const DocumentContext = createContext<DocumentContextType | undefined>(
  undefined,
);

export const SendToLendersContext = createContext<
  SendToLendersContextType | undefined
>(undefined);

export const ConditionsContext = createContext<
  ConditionsContextType | undefined
>(undefined);

export const useDocuments = () => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error("useDocuments must be used within ConditionsDialog");
  }
  return context;
};

export const useSendToLenders = () => {
  const context = useContext(SendToLendersContext);
  if (!context) {
    throw new Error("useSendToLenders must be used within ConditionsDialog");
  }
  return context;
};

export const useConditionsContext = () => {
  const context = useContext(ConditionsContext);
  if (!context) {
    throw new Error(
      "useConditionsContext must be used within ConditionsDialog",
    );
  }
  return context;
};

export default function ConditionsDialog({
  open,
  onOpenChange,
  onConfirm,
  lenders = [],
  applicationId = "",
  children,
}: ConditionsDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState<File[]>([]);
  const [selectedLenderIndices, setSelectedLenderIndices] = useState<number[]>(
    [],
  );
  const [conditions, setConditions] = useState<ApplicationCondition[]>([]);
  const [conditionFiles, setConditionFiles] = useState<Map<string, File>>(
    new Map(),
  );
  const [loadingConditions, setLoadingConditions] = useState(false);
  const [newConditions, setNewConditions] = useState<NewConditionInput[]>([]);

  // Initialize selected lenders with Porchpass when dialog opens or lenders change
  // Porchpass is the default lender, with fallback to first lender if not available
  useEffect(() => {
    if (open) {
      const porchpassIndex = lenders.findIndex(isPorchpassLender);
      if (porchpassIndex !== -1) {
        setSelectedLenderIndices([porchpassIndex]);
      } else if (lenders.length > 0) {
        setSelectedLenderIndices([0]);
      }
    }
  }, [open, lenders]);

  /**
   * Determines if only Porchpass is selected.
   * When true, shows condition-specific upload UI.
   * When false, shows bulk upload UI for any other lender.
   */
  const isOnlyPorchpass =
    selectedLenderIndices.length === 1 &&
    isPorchpassLender(lenders[selectedLenderIndices[0]]);

  /**
   * Fetch conditions from API only when Porchpass is the sole selected lender.
   * This ensures we have the specific conditions to display in the UI.
   */
  const {
    conditions: fetchedConditions,
    loading: fetchingConditions,
    error: conditionsError,
  } = useConditions(applicationId, undefined, isOnlyPorchpass);

  // Update local state when conditions are fetched or loading state changes
  useEffect(() => {
    if (fetchedConditions && fetchedConditions.length > 0) {
      setConditions(fetchedConditions);
    }
    setLoadingConditions(fetchingConditions);
  }, [fetchedConditions, fetchingConditions]);

  const addDocument = (file: File) => {
    setDocuments((prev) => [...prev, file]);
  };

  const removeDocument = (fileName: string) => {
    setDocuments((prev) => prev.filter((doc) => doc.name !== fileName));
  };

  const clearDocuments = () => {
    setDocuments([]);
  };

  const toggleLender = (index: number) => {
    setSelectedLenderIndices((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const clearSelectedLenders = () => {
    setSelectedLenderIndices([]);
  };

  const setConditionFile = (conditionId: string, file: File | null) => {
    const newMap = new Map(conditionFiles);
    if (file) {
      newMap.set(conditionId, file);
    } else {
      newMap.delete(conditionId);
    }
    setConditionFiles(newMap);
  };

  const addNewCondition = (condition: NewConditionInput) => {
    setNewConditions((prev) => [...prev, condition]);
  };

  const removeNewCondition = (title: string) => {
    setNewConditions((prev) => prev.filter((c) => c.title !== title));
  };

  // Determine if we should enable submit button
  const canSubmit = isOnlyPorchpass
    ? conditionFiles.size > 0 || newConditions.length > 0
    : documents.length > 0;

  /**
   * Handles the confirm action, delegating to parent component.
   * Has two paths:
   * - Porchpass: Send condition-specific files and/or new conditions
   * - Other lenders: Send bulk documents with no condition mapping
   */
  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const selectedLenders = selectedLenderIndices
        .map((index) => lenders[index])
        .filter((lender) => lender !== undefined);

      if (isOnlyPorchpass) {
        await onConfirm(
          [],
          selectedLenders,
          conditionFiles,
          conditions,
          newConditions,
        );
      } else {
        await onConfirm(
          documents,
          selectedLenders,
          undefined,
          conditions,
          undefined,
        );
      }

      clearDocuments();
      setConditionFiles(new Map());
      setNewConditions([]);
      clearSelectedLenders();
      onOpenChange(false);
    } catch {
      // Parent throws to keep dialog open (e.g. retryable 5xx).
      // Toast is fired by the parent; we just stay open.
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DocumentContext.Provider
      value={{ documents, addDocument, removeDocument, clearDocuments }}
    >
      <SendToLendersContext.Provider
        value={{
          lenders,
          selectedLenderIndices,
          toggleLender,
          clearSelectedLenders,
        }}
      >
        <ConditionsContext.Provider
          value={{
            conditions,
            setConditions,
            conditionFiles,
            setConditionFile,
            loadingConditions,
            newConditions,
            addNewCondition,
            removeNewCondition,
          }}
        >
          <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[440px] rounded-[20px] p-0 pb-2 gap-0 flex flex-col max-h-[85vh]">
              <DialogHeader className="border-b border-[#e4e7ec] px-6 py-4">
                <div className="flex flex-col gap-1">
                  <DialogTitle className="text-[19px] font-[650] text-[#111827]">
                    Upload documents
                  </DialogTitle>
                  <p className="text-base font-normal text-[#4A5466]">
                    {isOnlyPorchpass && conditions.length > 0
                      ? "Resolve conditions by uploading required documents."
                      : "Choose who it's going to, then add your file(s)."}
                  </p>
                </div>
              </DialogHeader>
              <div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
                {isOnlyPorchpass && conditionsError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-red-800">
                      Unable to load conditions
                    </p>
                    <p className="text-sm text-red-700 mt-1">
                      The application is not yet available in Digifi. Please try
                      again later or contact support if the problem persists.
                    </p>
                  </div>
                )}
                {children}
              </div>
              <DialogFooter className="gap-4 justify-end px-6 pt-0 pb-0">
                <DialogClose asChild>
                  <Button variant="ghost" size="s" disabled={isLoading}>
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  variant="primary"
                  size="s"
                  onClick={() => void handleConfirm()}
                  innerClassName="gap-2"
                  loading={isLoading}
                  disabled={
                    isLoading ||
                    !canSubmit ||
                    !isOnlyPorchpass ||
                    (isOnlyPorchpass && !!conditionsError)
                  }
                >
                  {isOnlyPorchpass && conditions.length > 0
                    ? `Submit ${conditionFiles.size} file${conditionFiles.size !== 1 ? "s" : ""}`
                    : `Submit${documents.length > 0 ? ` ${documents.length} document${documents.length > 1 ? "s" : ""}` : ""}`}
                </Button>
              </DialogFooter>
              {!isOnlyPorchpass && (
                <div className="text-sm font-medium text-red-600 text-right px-2 pt-2">
                  <p>
                    Only sending conditions to Porchpass is currently supported.
                  </p>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </ConditionsContext.Provider>
      </SendToLendersContext.Provider>
    </DocumentContext.Provider>
  );
}
